import React, { useMemo } from "react";
import ReactSelect from "react-select";

const MENU_PORTAL_Z_INDEX = 99999;

const baseStyles = {
  indicatorSeparator: () => ({ display: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: MENU_PORTAL_Z_INDEX }),
  menuList: (base) => ({
    ...base,
    maxHeight: 280,
    paddingTop: 4,
    paddingBottom: 4,
  }),
};

const controlStyles = (state, hasError, variant) => {
  const isCampaign = variant === "campaign";
  const focusBorder = hasError ? "#fca5a5" : isCampaign ? "#111827" : "#059669";
  const hoverBorder = hasError ? "#fca5a5" : state.isFocused ? focusBorder : "#d1d5db";
  const focusRing = hasError
    ? "0 0 0 2px rgba(248, 113, 113, 0.35)"
    : isCampaign
      ? "0 0 0 2px rgba(17, 24, 39, 0.08)"
      : "0 0 0 2px rgba(5, 150, 105, 0.2)";

  return {
    minHeight: 44,
    fontSize: 14,
    fontWeight: 500,
    borderColor: hasError ? "#fca5a5" : state.isFocused ? focusBorder : "#e5e7eb",
    borderRadius: 12,
    boxShadow: state.isFocused ? focusRing : "none",
    backgroundColor: state.isDisabled ? "#f9fafb" : "white",
    "&:hover": {
      borderColor: hoverBorder,
    },
  };
};

const optionStyles = (base, state, variant) => {
  const isCampaign = variant === "campaign";
  return {
    ...base,
    fontSize: 14,
    padding: "10px 14px",
    backgroundColor: state.isSelected
      ? isCampaign
        ? "rgba(238, 250, 148, 0.4)"
        : "#ecfdf5"
      : state.isFocused
        ? isCampaign
          ? "#e9eeea"
          : "#f3f4f6"
        : "white",
    color: state.isSelected
      ? isCampaign
        ? "#111827"
        : "#047857"
      : "#111827",
  };
};

/**
 * Searchable single- or multi-select (react-select) — campaign forms, filters, etc.
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  isMulti = false,
  placeholder = "Search…",
  isLoading = false,
  isDisabled = false,
  isClearable = false,
  error = false,
  noOptionsMessage = () => "No options found",
  formatOptionLabel,
  closeMenuOnSelect = !isMulti,
  hideSelectedOptions = false,
  maxMenuHeight = 280,
  variant = "emerald",
}) {
  const styles = useMemo(
    () => ({
      ...baseStyles,
      control: (base, state) => ({
        ...base,
        ...controlStyles(state, error, variant),
      }),
      valueContainer: (base) => ({
        ...base,
        padding: isMulti ? "4px 10px" : "0 14px",
      }),
      input: (base) => ({ ...base, margin: 0, padding: 0 }),
      dropdownIndicator: (base) => ({ ...base, padding: "0 10px", color: "#9ca3af" }),
      clearIndicator: (base) => ({ ...base, padding: "0 6px", color: "#9ca3af" }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "#f3f4f6",
        borderRadius: 6,
        border: "1px solid #e5e7eb",
        margin: 2,
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: "#374151",
        fontSize: 13,
        fontWeight: 600,
        padding: "1px 6px",
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#9ca3af",
        borderRadius: 4,
        ":hover": { backgroundColor: "#fee2e2", color: "#dc2626" },
      }),
      option: (base, state) => optionStyles(base, state, variant),
      singleValue: (base) => ({ ...base, color: "#111827" }),
      placeholder: (base) => ({ ...base, color: "#9ca3af" }),
    }),
    [error, isMulti, variant]
  );

  const filterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const q = inputValue.toLowerCase().trim();
    const searchText = option.data?.searchText || option.label?.toLowerCase() || "";
    return searchText.includes(q) || String(option.value).toLowerCase().includes(q);
  };

  return (
    <ReactSelect
      options={options}
      value={value}
      onChange={onChange}
      isMulti={isMulti}
      isSearchable
      isClearable={isClearable}
      isLoading={isLoading}
      isDisabled={isDisabled}
      placeholder={placeholder}
      filterOption={filterOption}
      maxMenuHeight={maxMenuHeight}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPlacement="auto"
      menuPosition="fixed"
      closeMenuOnSelect={closeMenuOnSelect}
      hideSelectedOptions={hideSelectedOptions}
      noOptionsMessage={noOptionsMessage}
      formatOptionLabel={formatOptionLabel}
      styles={styles}
    />
  );
}
